import { Box, Group, Menu, Stack, createStyles } from '@mantine/core';
import { Text } from '@repo/foundations';
import { getTextWidth } from '@repo/foundations/components/Skeleton/util';
import { transition } from '@repo/theme/primitives';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import type { ChartConfigHelper } from '../../../utils/charts/bi/ChartConfigHelper';
import type { LegendsInfo } from '../../../utils/charts/bi/types';
import { CHART_OPACITY } from '../constants';
const CONDENSED_DOTS_WIDTH = 21; // 3 dots * 6px spacing + 3px for the last dot (9px - 6px overlap)

const useStyles = createStyles(
	(
		theme,
		{
			chartConfigHelper,
			color,
			isHovering,
			hovered,
		}: {
			chartConfigHelper: ChartConfigHelper;
			color?: string;
			isHovering?: boolean;
			hovered?: boolean;
		}
	) => {
		let opacity: (typeof CHART_OPACITY)[keyof typeof CHART_OPACITY] =
			CHART_OPACITY.ACTIVE;
		if (isHovering && !hovered) {
			opacity = CHART_OPACITY.INACTIVE;
		}

		return {
			legendButton: {
				padding: `${theme.other.space[0.5]}px ${theme.other.space[1.5]}px`,
				borderRadius: theme.other.borderRadius.lg,
				height: 'auto',
				minHeight: 'unset',
				'& .mantine-Button-inner': {
					// override default button inner justify content to left align
					justifyContent: 'flex-start',
				},
			},
			legendItem: {
				cursor: 'pointer',
				flexWrap: 'nowrap',
				gap: theme.spacing.xs,
				padding: `${theme.spacing['3xs']} ${theme.spacing.xs}`,
			},
			legendDot: {
				width: theme.other.space[2],
				height: theme.other.space[2],
				borderRadius: theme.other.borderRadius.sm,
				justifyContent: 'flex-start',
				backgroundColor: color,
				opacity,
				...transition('duration-200', 'ease')('opacity'),
			},
			overflowButton: {
				padding: `${theme.other.space[0.5]}px ${theme.other.space[1.5]}px`,
				borderRadius: theme.other.borderRadius.lg,
				height: 'auto',
				minHeight: 'unset',
			},
			condensedContainer: {
				cursor: 'pointer',
				gap: theme.spacing.xs,
				padding: `${theme.spacing['3xs']} ${theme.spacing.xs}`,
				borderRadius: theme.other.borderRadius.lg,
				backgroundColor: theme.other.getColor('surface/primary/default'),
				'&:hover': {
					backgroundColor: theme.other.getColor('surface/primary/hover'),
					'& $condensedDotsContainer $condensedDot': {
						borderColor: theme.other.getColor('surface/primary/hover'),
					},
				},
			},
			condensedDotsContainer: {
				position: 'relative',
				width: CONDENSED_DOTS_WIDTH,
				height: theme.other.space[2],
			},
			condensedDot: {
				position: 'absolute',
				width: 9, // theme doesn't have 2.25. 9 because 8px is the dot width + 1px for the border
				height: 9,
				borderRadius: '100%',
				border: `0.5px solid ${theme.other.getColor('surface/primary/default')}`,
			},
			popoverDropdown: {
				padding: `${theme.other.space[1]}px 0`,
			},
			legendGroup: {
				height: theme.other.space[8], // 32px
			},
			legendText: {
				fontWeight: chartConfigHelper.fontWeight as any,
				fontSize: `${chartConfigHelper.fontSize}px`,
				opacity,
				...transition('duration-200', 'ease')('opacity'),
			},
		};
	}
);

interface LegendItemProps {
	legend: string;
	info: LegendsInfo[string];
	hoveredLegendName: string | null;
	onLegendHover?: (legendKey: string | null) => void;
	style?: React.CSSProperties;
	chartConfigHelper: ChartConfigHelper;
}

function LegendItem({
	legend,
	info,
	hoveredLegendName,
	onLegendHover,
	chartConfigHelper,
}: LegendItemProps) {
	const { classes } = useStyles({
		chartConfigHelper,
		color: info.color,
		isHovering: !isNil(hoveredLegendName),
		hovered: hoveredLegendName === legend,
	});

	return (
		<Group
			key={legend}
			className={classes.legendItem}
			onMouseEnter={() => {
				onLegendHover?.(legend);
			}}
			onMouseLeave={() => {
				onLegendHover?.(null);
			}}
			spacing="xs"
		>
			<Box className={classes.legendDot} />
			<Text color="text/secondary/default" className={classes.legendText}>
				{info.shortDisplayValue ?? legend}
			</Text>
		</Group>
	);
}

interface ChartLegendProps {
	legends: LegendsInfo;
	onLegendHover?: (legendKey: string | null) => void;
	chartWidth: number;
	hoveredLegendName: string | null;
	chartConfigHelper: ChartConfigHelper;
}

export function ChartLegend({
	legends,
	onLegendHover,
	chartWidth,
	hoveredLegendName,
	chartConfigHelper,
}: ChartLegendProps) {
	const { classes } = useStyles({ chartConfigHelper });
	// Calculate how many legends can fit based on chart width
	const { visibleLegends, hiddenLegends } = useMemo(() => {
		const legendEntries = Object.entries(legends);

		const legendWidths = legendEntries.map(([legend, info]) => {
			const text = info.shortDisplayValue ?? legend;
			const textWidth =
				getTextWidth(text, {
					fontSize: `${chartConfigHelper.fontSize}px`,
					fontWeight: chartConfigHelper.fontWeight,
				}) || 0;

			// Add extra space for gap + dot
			return textWidth + 60;
		});

		const overflowButtonText = `+${legendEntries.length} more`;
		const overflowButtonTextWidth =
			getTextWidth(overflowButtonText, {
				fontSize: `${chartConfigHelper.fontSize}px`,
				fontWeight: chartConfigHelper.fontWeight,
			}) || 0;
		// Add space for condensed dots (3 dots * 6px each with overlap) + text + padding
		const overflowButtonWidth =
			CONDENSED_DOTS_WIDTH + overflowButtonTextWidth + 30;

		let totalWidth = 0;
		let visibleCount = 0;

		for (let i = 0; i < legendEntries.length; i += 1) {
			const currentWidth = totalWidth + legendWidths[i];
			const needsOverflowButton = i < legendEntries.length - 1;
			const requiredWidth = needsOverflowButton
				? currentWidth + overflowButtonWidth
				: currentWidth;

			if (requiredWidth < (chartWidth ?? 0)) {
				totalWidth = currentWidth;
				visibleCount = i + 1;
			} else {
				break;
			}
		}

		return {
			visibleLegends: legendEntries.slice(0, visibleCount),
			hiddenLegends: legendEntries.slice(visibleCount),
		};
	}, [legends, chartWidth, chartConfigHelper]);
	return (
		<Group spacing={0} position="center" className={classes.legendGroup}>
			{visibleLegends.map(([legend, info]) => (
				<LegendItem
					key={legend}
					legend={legend}
					info={info}
					hoveredLegendName={hoveredLegendName}
					onLegendHover={onLegendHover}
					chartConfigHelper={chartConfigHelper}
				/>
			))}
			{hiddenLegends.length > 0 && (
				<Menu
					position="bottom-start"
					shadow="md"
					trigger="hover"
					openDelay={100}
					closeDelay={400}
				>
					<Menu.Target>
						<Group className={classes.condensedContainer}>
							{/* Condensed color dots from first few hidden legends */}
							<Box className={classes.condensedDotsContainer}>
								{hiddenLegends.slice(0, 3).map(([legend, info], index) => (
									<Box
										key={legend}
										className={classes.condensedDot}
										sx={{
											left: index * 6, // Overlap dots by 4px (9px - 6px)
											backgroundColor: info.color,
											zIndex: index,
										}}
									/>
								))}
							</Box>
							<Text
								className={classes.legendText}
								color="text/secondary/default"
							>
								+{hiddenLegends.length} more
							</Text>
						</Group>
					</Menu.Target>
					<Menu.Dropdown className={classes.popoverDropdown}>
						<Stack spacing={0} px={4}>
							{hiddenLegends.map(([legend, info]) => (
								<LegendItem
									key={legend}
									legend={legend}
									info={info}
									hoveredLegendName={hoveredLegendName}
									onLegendHover={onLegendHover}
									chartConfigHelper={chartConfigHelper}
								/>
							))}
						</Stack>
					</Menu.Dropdown>
				</Menu>
			)}
		</Group>
	);
}
